import { native } from '@forest/native-services'
import { dialog } from '@forest/shared/solid'
import isNetworkError from 'is-network-error'
import { isHTTPError, isTimeoutError } from 'ky'
import {
  createEffect,
  createMemo,
  createResource,
  createSignal,
  Show,
} from 'solid-js'
import { DailyChallengeCard } from '~/components/daily-challenge-card'
import { DailyCheckinCard } from '~/components/daily-checkin-card'
import { FlyingCoinLayer } from '~/components/flying-coin'
import { Hero } from '~/components/hero'
import { SpecialChallengeCard } from '~/components/special-challenge-card'
import { SpecialChallengeDetail } from '~/components/special-challenge-detail'
import { TopNav } from '~/components/top-nav'
import { RewardFlow } from '~/features/reward-flow'
import { convertGrantedContentsToRewardItems } from '~/features/reward-flow/helper'
import { rewardFlow, type PreviewAnimState } from '~/features/reward-flow/store'
import { useChestSounds } from '~/lib/audio/chest-sounds'
import { useRewardSound } from '~/lib/audio/reward-sound'
import { createCountdown, createNow } from '~/lib/countdown'
import { getLocalizedMessage } from '~/lib/l10n'
import { inStep } from '~/lib/load-failure'
import { openLoadFailureDialog } from '~/lib/load-failure-dialog'
import { appVersion } from '~/lib/params'
import { openPlusPaywall } from '~/lib/paywall'
import {
  decidePlusTap,
  type PlusTapSurface,
  type ServiceLevelState,
} from '~/lib/plus-claim'
import { cn } from '~/lib/utils'
import { m } from '~/paraglide/messages'
import { useClaimRewardMutation } from '~/resources/claim-reward'
import { useBalanceQuery } from '~/resources/currency'
import { useFocusChallenges } from '~/resources/focus-challenges'
import { findTreeId, getItemType } from '~/resources/item-types'
import {
  isInSubscription,
  useServiceLevelStatusQuery,
} from '~/resources/service-level'
import {
  deriveSpecialChallengeCard,
  isCompleteSpecialChallengeConfig,
  useSpecialChallengeConfigQuery,
  useSpecialChallengeProgressQuery,
} from '~/resources/special-challenge'

export function Page() {
  const balance = useBalanceQuery()
  const { query, data } = useFocusChallenges()
  const serviceLevel = useServiceLevelStatusQuery()
  const claim = useClaimRewardMutation()
  // The chest whose claim is mid-resolution — i.e. its service-level refetch or
  // paywall open is in flight. Drives the chest's loading state and guards
  // against double-taps racing ahead of the reactive disabled state.
  const [resolvingChestId, setResolvingChestId] = createSignal<number | null>(
    null,
  )

  const serviceLevelState = (): ServiceLevelState => {
    if (serviceLevel.isSuccess && serviceLevel.data) {
      return {
        status: 'success',
        inSubscription: isInSubscription(serviceLevel.data),
      }
    }
    if (serviceLevel.isError) return { status: 'error' }
    return { status: 'pending' }
  }
  const { playPress, playRelease } = useChestSounds()
  const { playReward } = useRewardSound()

  const monthlyEnd = createCountdown(() => data()?.monthly?.due_at ?? null)
  const dailyReset = createCountdown(() => {
    const checkinSlot = data()?.checkinSlots[0]
    return checkinSlot?.progress?.due_at ?? null
  })

  const [plantName] = createResource(
    () => data()?.monthly?.challenge_difficulty.reward_type_id ?? null,
    (rewardTypeId) =>
      inStep('load_item_types', async () => {
        if (rewardTypeId == null) return ''
        const itemType = await getItemType(rewardTypeId)
        const treeId = findTreeId(itemType)
        if (treeId == null) return ''
        return (await getLocalizedMessage(`tree_type_${treeId}_title`)) ?? ''
      }),
  )

  // One effect per read, so a later failure of the other is still reported.
  createEffect(() => {
    if (query.error) openLoadFailureDialog(query.error, { closesPage: true })
  })
  createEffect(() => {
    if (balance.error) {
      openLoadFailureDialog(balance.error, { closesPage: true })
    }
  })

  const onPressStart = () => playPress()
  const onPressEnd = () => playRelease()

  const claimProgress = async (
    progressId: number,
    sourceXY: { x: number; y: number },
    opts?: { previewAnim?: PreviewAnimState | null; background?: string },
  ): Promise<boolean> => {
    if (claim.isPending) return false
    try {
      const resp = await claim.mutateAsync(progressId)
      const items = await convertGrantedContentsToRewardItems(
        resp.granted_contents,
      )
      if (items.length > 0) {
        await rewardFlow.start({ items, source: sourceXY, ...opts })
      }
      return true
    } catch (error) {
      if (isTimeoutError(error) || isNetworkError(error)) {
        dialog.open({
          title: m.dialog_no_internet_title(),
          description: m.dialog_no_internet_content(),
          button: m.btn_got_it(),
        })
      } else {
        dialog.open({
          title: m.dialog_something_went_wrong_title(),
          description: m.dialog_unexpected_error_content({
            error_code: isHTTPError(error)
              ? error.response.status
              : String(error),
          }),
          button: m.btn_got_it(),
        })
      }
      return false
    }
  }

  const onCheckinClick = (
    slotIndex: number,
    el: HTMLButtonElement,
    _e: MouseEvent,
  ) => {
    const slot = data()?.checkinSlots[slotIndex]
    if (!slot?.progress) return
    if (slot.state !== 'available') return
    const rect = el.getBoundingClientRect()
    claimProgress(slot.progress.id, {
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2,
    })
  }

  // A tap on a daily challenge card — either the chest or the card body. For a
  // Plus challenge the whole card is a paywall entry for non-subscribers; for a
  // subscriber (or any non-Plus card) only a ready chest claims, while the body
  // and a non-ready chest are inert. See decidePlusTap.
  const handlePlusTap = async (
    taskIndex: number,
    surface: PlusTapSurface,
    e: MouseEvent,
  ) => {
    const task = data()?.daily[taskIndex]
    if (!task) return
    // Guard against double-taps: a refetch / paywall open from a prior tap may
    // still be in flight before the reactive disabled state catches up, and a
    // second pass would fire a duplicate fetch, navigation, or error dialog.
    if (resolvingChestId() != null) return
    setResolvingChestId(task.progress.id)
    try {
      // Plus challenges gate on a subscription. Resolve the decision, retrying
      // the service-level fetch once if its state is unresolved.
      const decide = (hasRefetched: boolean) =>
        decidePlusTap({
          surface,
          chestState: task.chestState,
          requiresSubscription: task.requiresSubscription,
          serviceLevel: serviceLevelState(),
          hasRefetched,
        })
      let action = decide(false)
      // The refetch's own result: the query's store may not show it yet.
      let refetchError: unknown
      if (action === 'refetch') {
        refetchError = (await serviceLevel.refetch()).error
        action = decide(true)
      }
      if (action === 'ignore') return
      if (action === 'paywall') {
        await openPlusPaywall()
        return
      }
      if (action === 'error-dialog') {
        openLoadFailureDialog(refetchError ?? serviceLevel.error, {
          closesPage: false,
        })
        return
      }

      const chapter: 1 | 2 | 3 =
        task.chestLevel === 'lv1' ? 1 : task.chestLevel === 'lv2' ? 2 : 3
      claimProgress(
        task.progress.id,
        { x: e.clientX, y: e.clientY },
        { previewAnim: { variant: 'chestbox_open', chapter } },
      )
    } finally {
      setResolvingChestId(null)
    }
  }

  const specialConfig = useSpecialChallengeConfigQuery()
  const specialProgress = useSpecialChallengeProgressQuery(
    () => specialConfig.data?.activity_id ?? null,
  )
  // Keeps the card visible (Completed) after an in-session claim during the
  // grace period, where a fresh page entry would hide it.
  const [specialClaimed, setSpecialClaimed] = createSignal(false)
  const now = createNow()
  // The visible card and its config travel together: derive() only returns a
  // visible state off a complete config (the fetch response is unvalidated
  // JSON despite its cast), so the guard here never hides anything extra —
  // it carries that completeness to the type level for the consumers.
  const visibleSpecial = createMemo(() => {
    const config = specialConfig.data ?? null
    const card = deriveSpecialChallengeCard({
      config,
      progresses: specialProgress.data?.challenge_progresses ?? null,
      now: now(),
      claimedThisSession: specialClaimed(),
      appVersion,
    })
    if (card.kind === 'hidden' || !isCompleteSpecialChallengeConfig(config))
      return null
    return { card, config }
  })
  const visibleSpecialCard = () => visibleSpecial()?.card ?? null

  const specialClaiming = () => {
    const card = visibleSpecialCard()
    return (
      !!card &&
      card.kind !== 'upcoming' &&
      claim.isPending &&
      claim.variables === card.progressId
    )
  }

  // The detail view is view state over the hub — no URL, no history entry.
  // Deriving it from the visible card means it closes itself the moment the
  // state has no surface any more (the event ended under the user).
  const [specialDetailOpen, setSpecialDetailOpen] = createSignal(false)
  createEffect(() => {
    // Reset the flag on self-close, not just hide the view: a later event
    // becoming visible in-session must not reopen the detail uninvited.
    if (specialDetailOpen() && !visibleSpecialCard())
      setSpecialDetailOpen(false)
  })
  const specialDetail = createMemo(() =>
    specialDetailOpen() ? visibleSpecial() : null,
  )

  const onSpecialClaim = async (e: MouseEvent) => {
    const special = visibleSpecial()
    if (!special) return
    const { card, config } = special
    if (card.kind === 'upcoming' || card.bar !== 'claim') return
    playReward()
    // Set the flag before awaiting: the claim mutation invalidates the special
    // query the moment the PUT succeeds, and the refetch may land while the
    // reward reveal is still playing — without the flag the grace-period rule
    // would hide the card mid-flow. Rolled back if the claim failed (where the
    // flag has no visible effect anyway, since the status stays 'completed').
    setSpecialClaimed(true)
    const ok = await claimProgress(
      card.progressId,
      { x: e.clientX, y: e.clientY },
      { background: config.reward_reveal_bg },
    )
    if (!ok) setSpecialClaimed(false)
  }

  const onClaimMonthly = () => {
    const monthly = data()?.monthly
    if (!monthly) return
    if (data()?.monthlyClaimState !== 'ready') return
    playReward()
    claimProgress(monthly.id, {
      x: window.innerWidth / 2,
      y: window.innerHeight / 2,
    })
  }

  return (
    <div class="relative min-h-full overflow-x-clip bg-surface-mint">
      <TopNav
        coins={balance.data?.coin ?? 0}
        gems={balance.data?.gem ?? 0}
        loading={balance.isPending}
      />
      <Hero
        current={data()?.monthly?.progress ?? 0}
        total={data()?.monthly?.target ?? 25}
        plantName={plantName() ?? ''}
        claimState={data()?.monthlyClaimState ?? 'progress'}
        endSeconds={monthlyEnd().totalSeconds}
        imageUrl={
          data()?.monthly?.challenge_difficulty.challenge.image_url ?? ''
        }
        loading={query.isPending}
        onClaim={onClaimMonthly}
      />
      <section
        class={cn(
          'relative z-10 w-full bg-surface-mint',
          // Clear the fixed special-challenge banner (~140px card + 48px
          // bottom padding) so the last daily card can scroll above it.
          visibleSpecialCard() ? 'pb-[220px]' : 'pb-[120px]',
        )}
      >
        <div class="mx-auto flex w-full max-w-[640px] flex-col gap-[12px] px-[12px]">
          <DailyCheckinCard
            slots={data()?.checkinSlots ?? []}
            resetSeconds={dailyReset().totalSeconds}
            loading={query.isPending}
            claimingId={claim.isPending ? (claim.variables ?? null) : null}
            onPressStart={onPressStart}
            onPressEnd={onPressEnd}
            onClickBox={onCheckinClick}
          />
          <DailyChallengeCard
            tasks={data()?.daily ?? []}
            resetSeconds={dailyReset().totalSeconds}
            loading={query.isPending}
            claimingId={
              resolvingChestId() ??
              (claim.isPending ? (claim.variables ?? null) : null)
            }
            onTap={handlePlusTap}
          />
        </div>
      </section>
      <Show when={visibleSpecial()}>
        {(special) => (
          <SpecialChallengeCard
            card={special().card}
            config={special().config}
            claiming={specialClaiming()}
            onClaim={onSpecialClaim}
            onOpenDetail={() => setSpecialDetailOpen(true)}
          />
        )}
      </Show>
      <Show when={specialDetail()}>
        {(detail) => (
          <SpecialChallengeDetail
            card={detail().card}
            config={detail().config}
            claiming={specialClaiming()}
            onClaim={onSpecialClaim}
            onClose={() => setSpecialDetailOpen(false)}
          />
        )}
      </Show>
      <RewardFlow />
      <FlyingCoinLayer />
    </div>
  )
}
